import type { BundleMatch, Phase } from './wcTypes';
import { KNOCKOUT_PHASES } from './wcTypes';

export type Standing = {
  code: string;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  gf: number;
  ga: number;
  gd: number;
  pts: number;
};

/**
 * Group table from finished matches, ported from the app's groups_view.dart:
 * 3 pts per win, 1 per draw; sort by pts → goal difference → goals for.
 * Pre-tournament rows read all zeros. Top 2 (callers highlight) advance.
 */
export function computeStandings(teamCodes: string[], matches: BundleMatch[]): Standing[] {
  const table = new Map<string, Standing>();
  for (const code of teamCodes) {
    table.set(code, { code, played: 0, won: 0, drawn: 0, lost: 0, gf: 0, ga: 0, gd: 0, pts: 0 });
  }
  for (const m of matches) {
    if (m.status !== 'done' || m.score_a == null || m.score_b == null) continue;
    if (!m.team_a || !m.team_b) continue;
    const a = table.get(m.team_a);
    const b = table.get(m.team_b);
    if (!a || !b) continue;
    a.played++; b.played++;
    a.gf += m.score_a; a.ga += m.score_b;
    b.gf += m.score_b; b.ga += m.score_a;
    if (m.score_a > m.score_b) { a.won++; a.pts += 3; b.lost++; }
    else if (m.score_a < m.score_b) { b.won++; b.pts += 3; a.lost++; }
    else { a.drawn++; b.drawn++; a.pts++; b.pts++; }
  }
  const rows = [...table.values()];
  for (const r of rows) r.gd = r.gf - r.ga;
  rows.sort((x, y) => y.pts - x.pts || y.gd - x.gd || y.gf - x.gf || x.code.localeCompare(y.code));
  return rows;
}

export type Bracket = Record<Phase, BundleMatch[]>;

/** Knockout matches grouped by phase, each sorted by match number. */
export function computeBracket(matches: BundleMatch[]): Bracket {
  const out = {} as Bracket;
  for (const phase of KNOCKOUT_PHASES) {
    out[phase] = matches
      .filter((m) => m.phase === phase)
      .sort((a, b) => a.number - b.number);
  }
  return out;
}
